import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Download, FileText, AlertCircle, Building2 } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const REPORTS = [
  { key: 'wos_missing_po',          label: 'Work Orders Missing PO' },
  { key: 'po_required_not_received', label: 'PO Required But Not Received' },
  { key: 'po_received_not_invoiced', label: 'PO Received But Not Invoiced' },
  { key: 'invoices_missing_po',     label: 'Invoices Missing PO' },
];

const fmtDate = (s) => { try { return new Date(s).toLocaleString(); } catch { return s || '—'; } };

const PoReports = () => {
  const [active, setActive] = useState(REPORTS[0].key);
  const [range, setRange] = useState({ from: '', to: '' });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = { report: active };
      if (range.from) params.date_from = range.from;
      if (range.to)   params.date_to   = range.to;
      const { data } = await axios.get(`${API}/admin/pm/po-reports`, { params, withCredentials: true });
      setData(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load report');
    }
    setLoading(false);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [active]);

  const exportCsv = async () => {
    try {
      const params = { report: active, fmt: 'csv' };
      if (range.from) params.date_from = range.from;
      if (range.to)   params.date_to   = range.to;
      const res = await axios.get(`${API}/admin/pm/po-reports`, { params, withCredentials: true, responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url; a.download = `${active}-${new Date().toISOString().slice(0,10)}.csv`;
      a.click(); URL.revokeObjectURL(url);
    } catch {
      toast.error('CSV export failed');
    }
  };

  return (
    <div data-testid="admin-po-reports" className="p-6 space-y-6">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <p className="text-[#D4AF37] text-xs font-bold tracking-[0.3em] mb-1">PROPERTY MANAGEMENT</p>
          <h1 className="font-['Outfit'] text-3xl font-bold text-white">Purchase Order Reports</h1>
          <p className="text-[#A3A3A3] text-sm mt-1">Four key reports to keep invoicing on schedule.</p>
        </div>
        <button data-testid="po-report-csv" onClick={exportCsv} className="bg-[#D4AF37] text-[#0A0A0A] px-4 h-10 rounded font-medium hover:bg-[#F3E5AB] inline-flex items-center gap-2">
          <Download className="w-4 h-4" /> Export CSV
        </button>
      </div>

      {/* Tabs */}
      <div className="flex flex-wrap gap-2">
        {REPORTS.map((r) => (
          <button
            key={r.key}
            onClick={() => setActive(r.key)}
            data-testid={`po-report-tab-${r.key}`}
            className={`px-3 py-2 rounded text-sm border ${active === r.key ? 'border-[#D4AF37] bg-[#D4AF37]/10 text-[#D4AF37]' : 'border-[#262626] text-[#A3A3A3] hover:text-white'}`}
          >
            {r.label}
          </button>
        ))}
      </div>

      {/* Date range */}
      <div className="flex flex-wrap gap-2 items-end">
        <div>
          <label className="text-[#A3A3A3] text-xs block mb-1">From</label>
          <input type="date" value={range.from} onChange={(e) => setRange({ ...range, from: e.target.value })} className="bg-[#0A0A0A] border border-[#262626] rounded h-10 px-2 text-white text-sm" />
        </div>
        <div>
          <label className="text-[#A3A3A3] text-xs block mb-1">To</label>
          <input type="date" value={range.to} onChange={(e) => setRange({ ...range, to: e.target.value })} className="bg-[#0A0A0A] border border-[#262626] rounded h-10 px-2 text-white text-sm" />
        </div>
        <button onClick={load} className="bg-[#0A0A0A] border border-[#262626] hover:border-[#D4AF37] text-white px-4 h-10 rounded text-sm">Apply</button>
      </div>

      {/* Results */}
      <div className="bg-[#141414] border border-[#262626] rounded">
        <div className="px-4 py-3 border-b border-[#262626] flex items-center gap-2">
          <FileText className="w-4 h-4 text-[#D4AF37]" />
          <p className="text-white font-medium">{data?.title || 'Loading…'}</p>
          <span className="ml-auto text-[#525252] text-xs">{loading ? 'Loading…' : `${data?.count ?? 0} rows`}</span>
        </div>
        <table className="w-full text-sm">
          <thead className="bg-[#0A0A0A] text-[#525252] text-[10px] uppercase">
            <tr>
              <th className="p-3 text-left">WO #</th>
              <th className="p-3 text-left">Company</th>
              <th className="p-3 text-left">Property</th>
              <th className="p-3 text-left">Unit</th>
              <th className="p-3 text-left">Status</th>
              <th className="p-3 text-left">PO Status</th>
              <th className="p-3 text-left">PO #</th>
              <th className="p-3 text-left">Submitted</th>
            </tr>
          </thead>
          <tbody>
            {data?.rows?.length ? data.rows.map((r) => (
              <tr key={r.id} data-testid={`po-report-row-${r.id}`} className="border-t border-[#262626] hover:bg-[#1a1a1a]">
                <td className="p-3 text-[#D4AF37] font-mono">{r.work_order_number}</td>
                <td className="p-3 text-[#A3A3A3]">{r.company_name}</td>
                <td className="p-3 text-white">{r.property_name}</td>
                <td className="p-3 text-[#A3A3A3]">{r.unit_number || '—'}</td>
                <td className="p-3 text-white text-xs">{r.status}</td>
                <td className="p-3 text-xs">{r.po_status}</td>
                <td className="p-3 text-emerald-300 font-mono text-xs">{r.po_number || <span className="text-[#525252]">—</span>}</td>
                <td className="p-3 text-[#525252] text-xs">{fmtDate(r.created_at)}</td>
              </tr>
            )) : (
              <tr><td colSpan={8} className="p-6 text-center text-[#525252] text-sm">
                {loading ? 'Loading…' : 'No matching records — great news!'}
              </td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default PoReports;
